import { useState, useRef, useCallback, useEffect } from 'react';
import Webcam from 'react-webcam';
import { extractLicensePlate } from 'api/canpark/scanning/extractLicensePlate';
import { useNotificationProvider } from 'hooks';
import { normalizeLicensePlate } from '../utils/normalizeLicensePlate';
import { cropImage } from '../utils/cropImage';
import { useActiveVehicle } from '../context/ActiveVehicleProvider';
import { debounce } from 'lodash';

export const useCamera = (
	setCapturedImage: (val: string) => void,
	setIsCameraOn: (val: boolean) => void,
	setIsLoading: (val: boolean) => void,
	onCleanScan?: (plate: string, state: string) => void,
) => {
	const webcamRef = useRef<Webcam | null>(null);
	const [flashEnabled, setFlashEnabled] = useState(false);
	const [showScanAnimation, setShowScanAnimation] = useState(false);
	const [isWebcamLoaded, setIsWebcamLoaded] = useState(false);
	const [zoomLevel, setZoomLevel] = useState(1); // Default zoom (1x)

	const [minZoom, setMinZoom] = useState(1);
	const [maxZoom, setMaxZoom] = useState(3);

	const viewFinderBoxRef = useRef<HTMLDivElement | null>(null);

	const { notifyError, notifyWarning } = useNotificationProvider();
	const { setPendingScanImage } = useActiveVehicle();

	const capture = async (
		setLicensePlateNumber: (val: string) => void,
		setSelectedState: (val: string) => void,
	) => {
		if (!webcamRef.current) return;

		const imageSrc = webcamRef.current.getScreenshot();
		if (imageSrc) {
			const viewFinderWidth = viewFinderBoxRef?.current?.clientWidth;
			const viewFinderHeight = viewFinderBoxRef?.current?.clientHeight;

			const croppedImage = await cropImage(
				imageSrc,
				viewFinderWidth,
				viewFinderHeight,
			);
			setCapturedImage(croppedImage);
			setIsLoading(true);
			try {
				const data = await extractLicensePlate(croppedImage);

				// Check if scan returned empty results
				if (!data.LicensePlate && !data.LicenseState) {
					notifyWarning(
						'Unable to detect license plate. Please try again or enter manually.',
					);
				} else {
					const normalizedPlate = normalizeLicensePlate(
						data.LicensePlate,
					);
					setLicensePlateNumber(normalizedPlate);
					setSelectedState(data.LicenseState);
					setPendingScanImage(croppedImage);
					onCleanScan?.(normalizedPlate, data.LicenseState);
				}
			} catch (error) {
				console.error('[useCamera] Failed to extract license plate:', error);
				notifyError('Failed to extract license plate. Please try again.');
			} finally {
				setIsCameraOn(false);
				setIsLoading(false);
			}
		}
	};

	const toggleFlash = async () => {
		if (webcamRef.current && webcamRef.current.video) {
			const track = (
				webcamRef.current.video.srcObject as MediaStream
			)?.getVideoTracks()[0];

			if (!track) return;

			const capabilities = track?.getCapabilities?.();
			if (capabilities && 'torch' in capabilities) {
					try {
					const newFlashState = !flashEnabled; // Toggle state
					await track.applyConstraints({
						advanced: [{ torch: newFlashState }],
					} as any);

					// Only update state after successful constraint application
					setFlashEnabled(newFlashState);
				} catch (error) {
					console.error('[useCamera] Failed to toggle flash:', error);
				}
			} else {
				notifyError('Flash not supported on this device');
			}
		}
	};

	useEffect(() => {
		if (webcamRef.current && webcamRef.current.video) {
			const videoTrack = webcamRef.current.stream?.getVideoTracks()[0];
			if (videoTrack) {
				const capabilities = videoTrack.getCapabilities();
				if ('zoom' in capabilities) {
					const zoomCapabilities = capabilities.zoom as {
						min: number;
						max: number;
					};

					const zoomMin = 1;
					const zoomMax = Math.min(zoomCapabilities.max, 10); // Limit max zoom to 10

					setMinZoom(zoomMin);
					setMaxZoom(zoomMax);
				} else {
					setMinZoom(1);
					setMaxZoom(1);
				}
			}
		}
	}, [isWebcamLoaded]); // Run this after the webcam is loaded

	// Debounced applyZoom function to improve performance
	const debouncedApplyZoom = useCallback(
		debounce((zoomValue: number) => {
			if (webcamRef.current && webcamRef.current.video) {
				const videoTrack =
					webcamRef.current.stream?.getVideoTracks()[0];
				if (videoTrack) {
					const capabilities = videoTrack.getCapabilities();
					if ('zoom' in capabilities) {
						(videoTrack as any).applyConstraints({
							advanced: [{ zoom: zoomValue }],
						});
					}
				}
			}
		}, 100), // Debounce time (100ms)
		[webcamRef],
	);

	useEffect(() => {
		if (isWebcamLoaded) {
			setShowScanAnimation(true);
			const timer = setTimeout(() => setShowScanAnimation(false), 4000);
			return () => clearTimeout(timer);
		}
	}, [isWebcamLoaded]);

	return {
		webcamRef,
		isWebcamLoaded,
		showScanAnimation,
		setIsWebcamLoaded,
		debouncedApplyZoom,
		maxZoom,
		minZoom,
		zoomLevel,
		setZoomLevel,
		capture,
		toggleFlash,
		flashEnabled,
		viewFinderBoxRef,
	};
};
